import { z } from "zod";
import { PHASE1_CHECK_PACK_VERSION } from "./phase1-check-pack.js";
import { AgencyIdSchema, ProjectIdSchema, RevisionIdSchema, RunIdSchema, } from "../primitives/ids.js";
/** QA-008: runner + product-pack version pin on every completed QG envelope. */
export const QUALITY_GATE_CHECK_VERSION = PHASE1_CHECK_PACK_VERSION;
export const QualityGateEnvironmentSchema = z.enum(["local", "ci", "cloud-agent"]);
export const QualityGateStatusSchema = z.enum([
    "QA_PASS",
    "QA_WARN",
    "QA_FAIL",
    "QA_NOT_RUN",
]);
export const CheckSeveritySchema = z.enum([
    "INFO",
    "ADVISORY",
    "WARN",
    "FAIL",
    "BLOCKING",
    "SECURITY",
]);
export const CheckResultSchema = z
    .object({
    checkId: z.string().min(1),
    severity: CheckSeveritySchema,
    passed: z.boolean(),
    message: z.string().optional(),
})
    .strict();
const QualityGateResultV1BaseSchema = z
    .object({
    schemaVersion: z.literal("quality-gate-result.v1.0"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    artifactSetHash: z.string().min(1),
    status: QualityGateStatusSchema,
    checks: z.array(CheckResultSchema),
    completed: z.boolean(),
    completedAt: z.string().datetime().optional(),
    /** QA-001: graph revision bound at QG completion (required for completed shippable runs). */
    businessGraphRevisionId: RevisionIdSchema.optional(),
    visualQualityGateResult: z.unknown().optional(),
    accessibilityQualityPack: z.unknown().optional(),
    performanceQualityPack: z.unknown().optional(),
    searchReadinessQualityPack: z.unknown().optional(),
    privacyComplianceQualityPack: z.unknown().optional(),
    /** QA-008: execution environment bind (required when completed). */
    environment: QualityGateEnvironmentSchema.optional(),
    /** QA-008: check-pack / runner version bind (required when completed). */
    checkVersion: z.string().min(1).optional(),
})
    .strict();
const QualityGateResultV1InputSchema = z.preprocess((input) => {
    if (!input || typeof input !== "object") {
        return input;
    }
    const data = input;
    if (data.completed === true) {
        return {
            environment: "local",
            checkVersion: QUALITY_GATE_CHECK_VERSION,
            ...data,
        };
    }
    return data;
}, QualityGateResultV1BaseSchema);
export const QualityGateResultV1Schema = QualityGateResultV1InputSchema.superRefine((data, ctx) => {
    if (data.completed) {
        if (!data.environment) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "completed quality gate requires environment bind (QA-008)",
                path: ["environment"],
            });
        }
        if (!data.checkVersion) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "completed quality gate requires checkVersion bind (QA-008)",
                path: ["checkVersion"],
            });
        }
        if (!data.completedAt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "completed quality gate requires completedAt time bind (QA-008)",
                path: ["completedAt"],
            });
        }
    }
    if (!data.completed && data.status === "QA_PASS") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Partial QA run cannot produce QA_PASS (D-034)",
            path: ["status"],
        });
    }
    const resolved = resolveQualityGateStatus(data.checks, data.completed);
    if (data.status !== resolved) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `status must match resolved outcome (${resolved}), got ${data.status}`,
            path: ["status"],
        });
    }
});
/**
 * D-034: partial QA cannot produce QA_PASS.
 */
export function resolveQualityGateStatus(checks, completed) {
    if (!completed) {
        return "QA_NOT_RUN";
    }
    const hasBlocking = checks.some((c) => !c.passed &&
        (c.severity === "BLOCKING" || c.severity === "SECURITY" || c.severity === "FAIL"));
    if (hasBlocking) {
        return "QA_FAIL";
    }
    const hasWarn = checks.some((c) => !c.passed && c.severity === "WARN");
    if (hasWarn) {
        return "QA_WARN";
    }
    const allPassed = checks.every((c) => c.passed);
    if (!allPassed) {
        return "QA_FAIL";
    }
    return "QA_PASS";
}
/** Maps quality-gate outcome to generation-run terminal QA status (Lane A — Gap #2). */
export function runStatusFromQualityGateStatus(qaStatus) {
    if (qaStatus === "QA_PASS") {
        return "QA_PASSED";
    }
    if (qaStatus === "QA_WARN") {
        return "QA_PASSED_WITH_WARNINGS";
    }
    return "QA_FAILED";
}
export function isShippableGenerationRunStatus(status) {
    return status === "QA_PASSED" || status === "QA_PASSED_WITH_WARNINGS";
}
/**
 * Client exact-approval may bind a run that already passed QA.
 * Departure Kit export moves that same run to EXPORTED; export does not revoke the QA pass.
 * EXPORT_READY and EXPORTING stay ineligible because the export hash is not final yet.
 */
export function isClientApprovalEligibleRunStatus(status) {
    return isShippableGenerationRunStatus(status) || status === "EXPORTED";
}
export function assertCannotPassIfPartial(result) {
    const parsed = QualityGateResultV1Schema.safeParse(result);
    if (!parsed.success) {
        const partialPass = parsed.error.issues.some((i) => typeof i.message === "string" && i.message.includes("Partial QA"));
        if (partialPass) {
            throw new Error("Partial QA run cannot produce QA_PASS (D-034)");
        }
        throw new Error(parsed.error.message);
    }
}
//# sourceMappingURL=quality-gate-result.js.map